import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Dash, { DashProps } from './Dash'
import TitleDescription, { TitleDescriptionProps } from './TitleDescription'
import SubTitleDescription, { SubTitleDescriptionProps } from './SubTitleDescription'
import EmphasizedDescription, { EmphasizedDescriptionProps } from './EmphasizedDescription'
import TitleDescriptionSmall, { TitleDescriptionSmallProps } from './TitleDescriptionSmall'
import ShowPriceDetail from './ShowPriceDetail'
import { TMemberClassBadge } from '~/api/services/member-class-api/index.type'
import { MemberClassDiscount } from '~/app/components-nds/common/member-class'
import styled from '~/app/components/styled'
import CouponContent, {
  CouponContentProps,
} from '~/app/components/order-list/details/InformationPanel/CouponContent'

/**
 * index component
 * author: soojin.oh
 */

export enum DataType {
  TitleDescription = 'TitleDescription',
  TitleDescriptionSmall = 'TitleDescriptionSmall',
  SubTitleDescription = 'SubTitleDescription',
  EmphasizedDescription = 'EmphasizedDescription',
  Dash = 'Dash',
  Dotted = 'Dotted',
  Custom = 'Custom',
  ShowDetail = 'ShowDetail',
  MemberClassDiscount = 'MemberClassDiscount',
  CouponContent = 'CouponContent',
}

export type Renderable =
  | string
  | number
  | ReactElement
  | ReactElement[]
  | JSX.Element
  | JSX.Element[]

export type TitleDescription = {
  type: DataType.TitleDescription
} & TitleDescriptionProps

export type TitleDescriptionSmall = {
  type: DataType.TitleDescriptionSmall
} & TitleDescriptionSmallProps

export type SubTitleDescription = {
  type: DataType.SubTitleDescription
} & SubTitleDescriptionProps

export type EmphasizedDescription = {
  type: DataType.EmphasizedDescription
} & EmphasizedDescriptionProps

export type Dash = {
  type: DataType.Dash
}
export type Dotted = {
  type: DataType.Dotted
} & DashProps

export type Custom = {
  type: DataType.Custom
  body: Renderable
}

export type ShowDetail = {
  type: DataType.ShowDetail
}

export type MemberClassDiscountInfo = {
  type: DataType.MemberClassDiscount
  memberClass: TMemberClassBadge
  discountAmount: number
}

export type CouponContent = {
  type: DataType.CouponContent
} & CouponContentProps

export type InformationFormat =
  | TitleDescription
  | SubTitleDescription
  | EmphasizedDescription
  | Dash
  | Dotted
  | TitleDescriptionSmall
  | Custom
  | ShowDetail
  | MemberClassDiscountInfo
  | CouponContent
// type definitions
type InformationProps = {
  information: Array<InformationFormat>
}

// 다른 행(padding: 0 2rem)과 우측 정렬을 맞추고, 이전 VipDiscountNotice처럼
// 위쪽(총 결제 금액)을 가리키는 말풍선 화살표를 재현한다
const MemberClassDiscountWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 2rem;
  padding-bottom: 0.8rem;
`
const MemberClassDiscountBubble = styled.div`
  position: relative;

  ::before {
    content: '';
    position: absolute;
    top: -0.4rem;
    right: 2rem;
    border-left: 0.4rem solid transparent;
    border-right: 0.4rem solid transparent;
    border-bottom: 0.4rem solid ${({ theme }) => theme.color.black_op80};
  }
`

// index component
const InformationPanel: React.FunctionComponent<InformationProps> = function ({ information }) {
  const [showPriceDetail, setShowPriceDetail] = useState(true)

  useEffect(() => {
    setShowPriceDetail(!information.some(info => info.type === DataType.ShowDetail))
  }, [information])
  const contents = useMemo(
    () =>
      information.map((info, index) =>
        info.type === DataType.SubTitleDescription ? (
          <SubTitleDescription {...info} key={index} isShow={showPriceDetail} />
        ) : info.type === DataType.TitleDescription ? (
          <TitleDescription {...info} key={index} />
        ) : info.type === DataType.EmphasizedDescription ? (
          <EmphasizedDescription {...info} key={index} />
        ) : info.type === DataType.ShowDetail ? (
          <ShowPriceDetail key={index} isShow={showPriceDetail} setIsShow={setShowPriceDetail} />
        ) : info.type === DataType.Dash ? (
          <Dash key={index} />
        ) : info.type === DataType.Dotted ? (
          <Dash key={index} dotted />
        ) : info.type === DataType.TitleDescriptionSmall ? (
          <TitleDescriptionSmall {...info} key={index} />
        ) : info.type === DataType.Custom ? (
          <React.Fragment key={index}>{info.body}</React.Fragment>
        ) : info.type === DataType.MemberClassDiscount ? (
          info.memberClass !== 'SILVER_CLASS' && (
            <MemberClassDiscountWrapper key={index}>
              <MemberClassDiscountBubble>
                <MemberClassDiscount
                  memberClass={info.memberClass}
                  discountAmount={info.discountAmount}
                />
              </MemberClassDiscountBubble>
            </MemberClassDiscountWrapper>
          )
        ) : info.type === DataType.CouponContent ? (
          <CouponContent key={index} isShow={showPriceDetail} {...info} />
        ) : null
      ),
    [information, showPriceDetail]
  )
  return <>{contents}</>
}

export default InformationPanel
